import type { Session } from './api/auth'
import type { NavSubject } from './nav'

/**
 * The session, as the navigation needs to read it (D-322).
 *
 * **A function rather than a spread, because the two types disagree on
 * purpose.** Every flag on `Session` is optional — it was written defensively
 * against a response that might not carry them — while `NavSubject` requires
 * all four, because a maybe-boolean gate is a gate nobody can reason about.
 * Spreading one into the other would hand `undefined` to four checks and read
 * as correct at the call site.
 *
 * **An absent flag denies rather than passes** — the same direction D-83 chose
 * for scoping, and the one that costs least when it is wrong: a destination
 * missing is a support call, a destination that should not be there is a screen
 * that refuses. (`=== true` rather than `!!` is a statement of that intent
 * rather than a behavioural difference; these arrive as JSON booleans, so no
 * input distinguishes the two.)
 *
 * **`isKiosk` is the one that reads the other way, and it is deliberate.**
 * Absent means *not* a kiosk, so a malformed session gets the full rail rather
 * than none — because the real boundary is `.AllowKiosk()` on the server, which
 * refuses regardless of what the rail shows. Denying here instead would leave
 * every ordinary user with no navigation over a field that failed to
 * deserialise, which is a worse failure and a more likely one.
 */
export function navSubjectFrom(session: Session): NavSubject {
  return {
    capabilities: session.capabilities ?? [],
    isStaff: session.isStaff === true,
    isParent: session.isParent === true,
    isDancer: session.isDancer === true,
    teaches: session.teaches === true,
    ownsClasses: session.ownsClasses === true,
    isKiosk: session.isKiosk === true,

    /*
     * Off unless the host says otherwise (D-625), which is the same direction
     * every flag above takes and matters more here: an absent module reads as
     * a module nobody switched on, and the endpoints refuse anyway.
     */
    modules: { messaging: session.modules?.messaging === true },
  }
}
